import React, { useEffect, useState, useMemo } from "react";
import CommonDataTable from "../../components/CommonDataTable";
import { IconButton, Menu, MenuItem } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { fetchAppointments } from "../../services/appointment/appointment";
import { normalizeAppointmentTypeFromApi } from "./formFields/appointmentFields";

const mapAppointmentRows = (apiData = []) =>
    apiData.map((item) => {
        const h = item.AppointmentHeader || {};
        const typeRaw = h.appointmenttype ?? h.AppointmentType;
        const typeLabel = normalizeAppointmentTypeFromApi(typeRaw);
        return {
            id: item._id,
            __raw: item,
            Warehouse: h.Warehouse ?? "",
            AppointmentKey: h.AppointmentKey ?? "",
            AppointmentType:
                typeLabel === "Inbound" || typeLabel === "Outbound"
                    ? typeLabel
                    : String(typeRaw ?? ""),
            Status: h.Status ?? "",
            Door: h.Door ?? "",
            Carrier: h.Carrier ?? "",
            Trailer: h.Trailer ?? "",
            GMTStartDateAndTime: h.GMTStartDateAndTime ?? "",
            GMTEndDateAndTime: h.GMTEndDateAndTime ?? "",
            Owner: h.Owner ?? "",
            addwho: h.addwho ?? "",
            editwho: h.editwho ?? "",
        };
    });

const baseColumns = [
    { field: "AppointmentKey", headerName: "Appointment Key", width: 150 },
    { field: "Warehouse", headerName: "Warehouse", width: 120 },
    { field: "AppointmentType", headerName: "Type", width: 110 },
    { field: "Status", headerName: "Status", width: 100 },
    { field: "Door", headerName: "Door", width: 100 },
    { field: "Carrier", headerName: "Carrier", width: 120 },
    { field: "Trailer", headerName: "Trailer", width: 120 },
    { field: "GMTStartDateAndTime", headerName: "Start (GMT)", width: 170 },
    { field: "GMTEndDateAndTime", headerName: "End (GMT)", width: 170 },
    { field: "Owner", headerName: "Owner", width: 100 },
    { field: "addwho", headerName: "Added By", width: 140 },
    { field: "editwho", headerName: "Edited By", width: 140 },
];

export default function AppointmentList({ onView, onCreate }) {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [menuRow, setMenuRow] = useState(null);

    const loadData = async (pageNo, size) => {
        try {
            const res = await fetchAppointments(pageNo, size);
            const list = res?.data || [];
            const totalCount = res?.totalCount || 0;
            setRows(mapAppointmentRows(list));
            setTotal(totalCount);
        } catch {
            setRows([]);
            setTotal(0);
        }
    };

    useEffect(() => {
        loadData(page, pageSize);
    }, [page, pageSize]);

    const handleDataRefresh = async (payload = {}) => {
        const p = payload.page !== undefined ? payload.page : page;
        const ps = payload.pageSize !== undefined ? payload.pageSize : pageSize;
        await loadData(p, ps);
    };

    const actionColumn = useMemo(
        () => ({
            field: "__actions",
            headerName: "",
            width: 50,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params) => (
                <IconButton
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        setMenuAnchorEl(e.currentTarget);
                        setMenuRow(params.row);
                    }}
                >
                    <MoreVertIcon fontSize="small" />
                </IconButton>
            ),
        }),
        []
    );

    return (
        <>
            <CommonDataTable
                id="appointment-list"
                allPossibleColumns={[actionColumn, ...baseColumns]}
                initialVisible={["__actions", ...baseColumns.map((c) => c.field)]}
                initialRows={rows}
                rowCount={total}
                page={page}
                pageSize={pageSize}
                onPageChange={(newPage) => setPage(newPage)}
                onPageSizeChange={(newSize) => {
                    setPageSize(newSize);
                    setPage(0);
                }}
                height={500}
                showCreateButton
                createButtonText="Create New Appointment"
                createButtonIcon="bi-plus-lg"
                onCreateClick={() => onCreate?.()}
                checkboxSelection
                enableImportButton={false}
                enableExportButton={false}
                forInventory
                onDataRefresh={handleDataRefresh}
                readOnly
                linkColumns={{
                    AppointmentKey: {
                        entity: "Appointment",
                        underline: "always",
                        parseValues: (val) => {
                            if (val == null || val === "") return [];
                            return [String(val).trim()].filter(Boolean);
                        },
                    },
                }}
                onLinkOpen={({ row }) => {
                    if (row?.id != null && row.id !== "") {
                        onView?.(row.id);
                    }
                }}
            />

            <Menu
                open={Boolean(menuAnchorEl)}
                anchorEl={menuAnchorEl}
                onClose={() => setMenuAnchorEl(null)}
            >
                <MenuItem
                    onClick={() => {
                        onView(menuRow.id);
                        setMenuAnchorEl(null);
                    }}
                >
                    <i className="bi bi-eye me-2" />
                    View
                </MenuItem>
            </Menu>
        </>
    );
}
